import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../config';
import DigestView from './DigestView';
import { CheckCircle2, ChevronDown, ChevronRight, Circle, ClipboardCheck, FileText, Flag, Loader2, PlayCircle } from 'lucide-react';

const WS_STATUS = {
  pending: { label: 'Pending', cls: 'bg-slate-100 text-slate-500 border-slate-200', Icon: Circle },
  in_progress: { label: 'In Progress', cls: 'bg-blue-50 text-blue-700 border-blue-200', Icon: PlayCircle },
  complete: { label: 'Complete', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', Icon: CheckCircle2 },
  flagged: { label: 'Flagged', cls: 'bg-rose-50 text-rose-700 border-rose-200', Icon: Flag },
};

const ITEM_STATUSES = [
  { value: 'pending', label: 'Pending' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'complete', label: 'Complete' },
  { value: 'flagged', label: 'Flagged' },
];

/**
 * Structured due diligence workstreams (Commercial / Financial / Management /
 * Legal) for one deal — canonical template items with status, owner, and
 * findings. All data comes from the DD workspace; nothing is invented here.
 */
export default function DDWorkstreams({ dealId, dealName, refreshSignal }) {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState(null);
  const [openWs, setOpenWs] = useState(null);
  const [findingsDraft, setFindingsDraft] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/api/dd/deals/${dealId}`);
      setDoc(r.data);
      setError('');
    } catch (e) {
      if (e.response?.status === 404) setDoc(null);
      else setError(e.response?.data?.detail || 'Could not load the workstreams.');
    } finally {
      setLoading(false);
    }
  }, [dealId]);

  useEffect(() => { load(); }, [load]);

  // The Deal Review header owns "Refresh analysis & reports"; when it runs, the
  // mounted workstreams reload with the freshly analyzed findings.
  const lastSignal = useRef(refreshSignal ?? 0);
  useEffect(() => {
    if (refreshSignal === undefined || refreshSignal === lastSignal.current) return;
    lastSignal.current = refreshSignal;
    load().then(() => runSummary());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshSignal]);

  const start = async () => {
    setBusy(true);
    try {
      const r = await api.post('/api/dd/deals', {
        deal_id: dealId,
        deal_name: dealName || doc?.deal_name || dealId,
      });
      setDoc(r.data);
      // A DD workspace needs a deal name; fetch the deal for it if missing.
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not start the workstreams.');
    } finally {
      setBusy(false);
    }
  };

  const updateItem = async (wsId, item, patch) => {
    setBusy(true);
    try {
      const r = await api.put(`/api/dd/deals/${dealId}/items/${wsId}/${item.id}`, patch);
      setDoc(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not update the item.');
    } finally {
      setBusy(false);
    }
  };

  const runSummary = async () => {
    setBusy(true);
    setSummary(null);
    try {
      const r = await api.post(`/api/dd/deals/${dealId}/findings-summary`);
      setSummary(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not summarize findings.');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-2 text-sm text-slate-400">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading diligence workstreams…
      </div>
    );
  }

  const workstreams = Object.entries(doc?.workstreams || {});
  const progress = workstreams.length
    ? (() => {
        let done = 0, total = 0;
        workstreams.forEach(([, ws]) => {
          (ws.items || []).forEach((it) => { total += 1; if (it.status === 'complete') done += 1; });
        });
        return { done, total, pct: total ? Math.round((done / total) * 100) : 0 };
      })()
    : null;

  return (
    <div className="bg-white rounded-xl border border-slate-200">
      <div
        className="w-full flex items-center justify-between p-4 text-left"
      >
        <div className="flex items-center gap-2 font-semibold text-slate-800">
          <ClipboardCheck className="w-5 h-5 text-brand-600" />
          Diligence Workstreams
          {progress && (
            <span className="text-xs font-normal text-slate-400">
              {progress.done}/{progress.total} complete
            </span>
          )}
        </div>
        {doc && (
          <div className="flex items-center gap-3">
            <button
              onClick={runSummary}
              disabled={busy}
              className="btn-secondary text-xs py-1.5 px-3 inline-flex items-center gap-1.5"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
              Findings summary
            </button>
          </div>
        )}
      </div>

      {error && <div className="mx-4 mb-3 text-xs text-rose-600">{error}</div>}

      {!doc ? (
        <div className="p-4 pt-0 text-sm text-slate-500">
          <p className="mb-3">
            Run structured due diligence across four standard workstreams — Commercial, Financial,
            Management, and Legal — with a canonical checklist for each.
          </p>
          <button onClick={start} disabled={busy} className="btn-primary text-xs py-2 px-4 inline-flex items-center gap-2">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Start Diligence Workstreams
          </button>
        </div>
      ) : (
        <div className="px-4 pb-4">
          {progress && (
            <div className="mb-3">
              <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-brand-600 rounded-full transition-all" style={{ width: `${progress.pct}%` }} />
              </div>
              <div className="text-xs text-slate-400 mt-1">{progress.pct}% of diligence items complete</div>
            </div>
          )}
          {summary && (
            <div className="mb-3">
              <div className="text-xs font-semibold text-slate-700 mb-1">Findings summary</div>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                <DigestView
                  text={summary?.summary?.text || summary?.summary_text || summary?.text || ''}
                  emptyLabel="No findings recorded yet — they appear as diligence items are answered."
                  maxHeight="16rem"
                />
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
            {workstreams.map(([wsId, ws]) => {
              const open = openWs === wsId;
              return (
                <div key={wsId} className="border border-slate-200 rounded-lg overflow-hidden">
                  <button
                    className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 text-left"
                    onClick={() => setOpenWs(open ? null : wsId)}
                  >
                    <span className="text-sm font-semibold text-slate-700">{ws.title || wsId}</span>
                    <span className="flex items-center gap-2">
                      <span className="text-xs text-slate-400">
                        {(ws.items || []).filter((i) => i.status === 'complete').length}/{(ws.items || []).length}
                      </span>
                      {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                    </span>
                  </button>
                  {open && (
                    <div className="p-3 space-y-2">
                      {(ws.items || []).map((item) => {
                        const st = WS_STATUS[item.status] || WS_STATUS.pending;
                        const findingKey = `${wsId}:${item.id}`;
                        return (
                          <div key={item.id} className="text-xs border border-slate-100 rounded-lg p-2.5">
                            <div className="flex items-center justify-between gap-2">
                              <span className="font-medium text-slate-700">{item.label}</span>
                              <select
                                value={item.status}
                                disabled={busy}
                                onChange={(e) => updateItem(wsId, item, { status: e.target.value })}
                                className={`border rounded px-1.5 py-0.5 text-xs ${st.cls}`}
                              >
                                {ITEM_STATUSES.map((s) => (
                                  <option key={s.value} value={s.value}>{s.label}</option>
                                ))}
                              </select>
                            </div>
                            {item.finding && (
                              <div className="mt-1.5 text-slate-500 italic">“{item.finding}”</div>
                            )}
                            <div className="mt-1.5 flex items-start gap-2">
                              <input
                                value={findingsDraft[findingKey] ?? item.finding ?? ''}
                                onChange={(e) => setFindingsDraft((m) => ({ ...m, [findingKey]: e.target.value }))}
                                placeholder="Add a finding…"
                                className="flex-1 border border-slate-200 rounded px-2 py-1"
                                disabled={busy}
                              />
                              <button
                                onClick={() =>
                                  updateItem(wsId, item, {
                                    finding: findingsDraft[findingKey] ?? item.finding ?? '',
                                  })
                                }
                                disabled={busy}
                                className="btn-secondary text-xs py-1 px-2"
                              >
                                Save
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
